<template>
  <a
    v-if="href"
    :href="href"
    :class="buttonClass"
    :target="target"
    :rel="rel"
    @click="emit('click', $event)"
  >
    <i v-if="icon" :class="[iconClass, !iconOnly && 'me-2']"></i>
    <slot />
  </a>
  <RouterLink v-else-if="to" :to="to" :class="buttonClass" @click="emit('click', $event)">
    <i v-if="icon" :class="[iconClass, !iconOnly && 'me-2']"></i>
    <slot />
  </RouterLink>
  <button
    v-else
    :type="type"
    :class="buttonClass"
    :disabled="disabled"
    @click="emit('click', $event)"
  >
    <i v-if="icon" :class="[iconClass, !iconOnly && 'me-2']"></i>
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ButtonProps } from './types';

const props = withDefaults(defineProps<ButtonProps>(), {
  type: 'button',
  variant: 'primary',
  buttonStyle: 'filled',
  icon: undefined,
  iconOnly: false,
  disabled: false,
  dimmedWhenDisabled: false,
  customSize: false,
  rounded: true,
  size: 'normal',
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClass = computed(() => {
  const variant = props.variant;
  const style = props.buttonStyle;
  const sizeValue = props.size ?? 'normal';

  const sizeClassResolved = props.fontSizeClass || (sizeValue === 'large' ? 'size-14' : 'size-12');

  const variantClass = `custom-btn--${variant}`;
  const styleClass = `custom-btn--${style}`;

  const classes = [
    'btn',
    'custom-btn',
    'd-flex',
    'align-items-center',
    'justify-content-center',
    'text-nowrap',
    'text-decoration-none',
    'transition-all',
    'duration-200',
    'fw-normal',
    variantClass,
    styleClass,
  ];

  if (sizeClassResolved) {
    classes.push(sizeClassResolved);
  }

  if (!props.customSize) {
    classes.push(`custom-btn--size-${sizeValue}`);
    if (sizeValue === 'normal') {
      classes.push('h-48x');
    }
  }

  if (props.iconOnly) {
    classes.push('custom-btn--icon-only', `custom-btn--icon-only-${sizeValue}`);
  }

  if (props.rounded !== false) {
    classes.push('rounded-5');
  }

  if (props.dimmedWhenDisabled) {
    classes.push('custom-btn--dimmed-when-disabled');
  }

  return classes;
});

const iconClass = computed(() => {
  if (!props.icon) {
    return '';
  }

  const iconMap: Record<string, string> = {
    envelope: 'fa-solid fa-envelope',
    'times-circle': 'fa-regular fa-circle-xmark',
    clear: 'fa-regular fa-circle-xmark',
    plus: 'fa-solid fa-plus',
    star: 'fa-regular fa-star',
    check: 'fa-solid fa-check',
    'paper-plane': 'fa-regular fa-paper-plane',
    paperclip: 'fa-solid fa-paperclip',
    /* fa-regular が未読込だと文字化けするため、両方 fa-solid で表示 */
    bookmark: 'fa-solid fa-bookmark',
    'bookmark-filled': 'fa-solid fa-bookmark',
  };

  const iconClass = iconMap[props.icon] || `fa-solid fa-${props.icon}`;

  return iconClass;
});
</script>

<style scoped lang="scss">
.custom-btn {
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  display: inline-flex;
}

.custom-btn.btn:disabled,
.custom-btn.btn.disabled,
fieldset:disabled .custom-btn.btn {
  opacity: 1;
}

.custom-btn.custom-btn--dimmed-when-disabled.btn:disabled,
.custom-btn.custom-btn--dimmed-when-disabled.btn.disabled,
fieldset:disabled .custom-btn.custom-btn--dimmed-when-disabled.btn {
  opacity: 0.8;
}

.custom-btn:focus:not(:focus-visible) {
  outline: none;
  box-shadow: none;
}

.custom-btn:focus-visible {
  outline: 2px solid $primary-blue;
  outline-offset: 2px;
}

.custom-btn--primary {
  &.custom-btn--filled {
    background-color: $primary-blue;
    border-color: $primary-blue;
    color: $white-01;

    &:hover,
    &:active,
    &:focus-visible {
      background-color: $primary-active;
      border-color: $primary-active;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $primary-blue;
      border-color: $primary-blue;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    color: $primary-blue;
    border-color: $primary-blue;
    background-color: $white-01;

    &:hover,
    &:focus-visible {
      background-color: $primary-blue;
      color: $white-01;
      border-color: $primary-blue;
    }

    &:active {
      background-color: $primary-active;
      border-color: $primary-active;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $primary-blue;
      background-color: transparent;
      border-color: $primary-blue;
    }
  }

  &.custom-btn--no-border {
    color: $primary-blue;
    background-color: transparent;
    border: none;

    &:hover {
      opacity: 0.8;
      color: $primary-blue;
    }

    &:active {
      opacity: 0.6;
      color: $primary-blue;
    }

    &:focus-visible {
      outline: 2px solid $primary-blue;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $primary-blue;
      background-color: transparent;
    }
  }
}

.custom-btn--secondary {
  &.custom-btn--filled {
    background: linear-gradient(96deg, $secondary 0%, $secondary-dark 100%);
    border-color: $secondary;
    color: $white-01;

    &:hover,
    &:active,
    &:focus-visible {
      background: linear-gradient(96deg, $secondary-hover 0%, $secondary-dark-hover 100%);
      border-color: $secondary;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background: linear-gradient(96deg, $secondary 0%, $secondary-dark 100%);
      border-color: $secondary;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    color: $secondary;
    border-color: $secondary;
    background-color: $white-01;

    &:hover,
    &:focus-visible {
      background: linear-gradient(96deg, $secondary 0%, $secondary-dark 100%);
      color: $white-01;
      border-color: $secondary;
    }

    &:active {
      background: linear-gradient(96deg, $secondary-hover 0%, $secondary-dark-hover 100%);
      border-color: $secondary;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $secondary;
      background-color: transparent;
      border-color: $secondary;
    }
  }

  &.custom-btn--no-border {
    color: $secondary;
    background-color: transparent;
    border: none;

    &:hover {
      opacity: 0.8;
      color: $secondary;
    }

    &:active {
      opacity: 0.6;
      color: $secondary;
    }

    &:focus-visible {
      outline: 2px solid $secondary;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $secondary;
      background-color: transparent;
    }
  }
}

.custom-btn--size-normal {
  padding: 12px 48px;
  border-radius: 32px;
  gap: 4px;
}

.custom-btn--size-small {
  padding: 8px 16px;
  border-radius: 24px;
  gap: 0;
}

.custom-btn--size-large {
  padding: 16px 48px;
  border-radius: 32px;
  gap: 10px;
}

.custom-btn--icon-only {
  padding: 0;
  justify-content: center;
}

.custom-btn--icon-only-small {
  width: 38px;
  height: 38px;
  border-radius: 19px;
}

.custom-btn--icon-only-normal {
  width: 48px;
  height: 48px;
  border-radius: 24px;
}

.custom-btn--icon-only-large {
  width: 56px;
  height: 56px;
  border-radius: 28px;
}

.custom-btn--gray {
  &.custom-btn--filled {
    background-color: $grey-60;
    border-color: $grey-60;
    color: $white-01;

    &:hover,
    &:active,
    &:focus-visible {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $grey-60;
      border-color: $grey-60;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    background-color: $white-01;
    border-color: $grey-60;
    color: $grey-60;

    &:hover,
    &:focus-visible {
      background-color: $grey-60;
      border-color: $grey-60;
      color: $white-01;
    }

    &:active {
      background-color: $grey-70;
      border-color: $grey-70;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $grey-60;
      background-color: $white-01;
      border-color: $grey-60;
    }
  }

  &.custom-btn--no-border {
    background-color: transparent;
    border-color: transparent;
    color: $grey-60;

    &:hover {
      opacity: 0.8;
      color: $grey-60;
    }

    &:active {
      opacity: 0.6;
      color: $grey-60;
    }

    &:focus-visible {
      outline: 2px solid $grey-60;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $grey-60;
      background-color: transparent;
    }
  }
}

.custom-btn--danger {
  &.custom-btn--filled {
    background-color: $pink-01;
    border-color: $pink-01;
    color: $white-01;

    &:hover,
    &:active,
    &:focus-visible {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      background-color: $pink-01;
      border-color: $pink-01;
      color: $white-01;
    }
  }

  &.custom-btn--outlined {
    background-color: $white-01;
    border-color: $pink-01;
    color: $pink-01;

    &:hover,
    &:focus-visible {
      background-color: $pink-01;
      border-color: $pink-01;
      color: $white-01;
    }

    &:active {
      background-color: $pink-03;
      border-color: $pink-03;
      color: $white-01;
    }

    &:disabled,
    &.disabled {
      color: $pink-01;
      background-color: $white-01;
      border-color: $pink-01;
    }
  }

  &.custom-btn--no-border {
    background-color: transparent;
    border-color: transparent;
    color: $pink-01;

    &:hover {
      opacity: 0.8;
      color: $pink-01;
    }

    &:active {
      opacity: 0.6;
      color: $pink-01;
    }

    &:focus-visible {
      outline: 2px solid $pink-01;
      outline-offset: 2px;
    }

    &:disabled,
    &.disabled {
      color: $pink-01;
      background-color: transparent;
    }
  }
}
</style>
